import { ArrowForwardIcon } from "@chakra-ui/icons";
import { GridItem, Image, Link, Text, useColorMode } from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { calibersLink, getCurrentColor } from "../../constants";
import { Helmet } from "react-helmet";
import useCookie from "../../hooks/useCookie";
import { useFacetMeta } from "../../hooks/useFacetMeta";
import { generateCaliberSeoUrl } from "../../utils/seoUrlGenerator";
import { useMemo } from "react";
const CaliberItem = ({ category, path, categoryName }) => {
  const location = useLocation()
  const { caliber, total_count, caliber_image, caliber_url } = category;
  const { colorMode } = useColorMode();
  const { cookies } = useCookie();
  const { facetMappings, mappingsReady, getId } = useFacetMeta();

  const urlObj = new URL(caliber_url);
  const caliber_seo_url = urlObj.pathname.split("/").filter(Boolean).pop();

  const pathSegments = useMemo(
    () => location.pathname.split("/").filter(Boolean),
    [location.pathname]
  );

  const brandSlug = useMemo(() => {
    if (pathSegments[0] === "brands" && pathSegments[1]) {
      return pathSegments[1];
    }
    return null;
  }, [pathSegments]);

  const brandId = useMemo(() => {
    if (!brandSlug) return null;

    const idFromMappings = mappingsReady ? getId?.(brandSlug, "brand") : null;
    if (idFromMappings) {
      return idFromMappings;
    }

    // Fallback for known brand slugs when mappings have not loaded yet
    if (brandSlug.toLowerCase() === "aguila") {
      return "10";
    }

    return null;
  }, [brandSlug, mappingsReady, getId]);

  // Generate SEO URL for caliber
  const seoCaliberUrl = useMemo(() => {
    const baseUrl = mappingsReady && facetMappings
      ? generateCaliberSeoUrl(caliber, categoryName, facetMappings, cookies.zip)
      : caliber_seo_url;

    if (!brandId) {
      return baseUrl;
    }

    try {
      const hasLeadingSlash = baseUrl.startsWith("/");
      const hasProtocol = /^https?:\/\//i.test(baseUrl);
      const urlWithBase = hasProtocol ? baseUrl : `${window.location.origin}${hasLeadingSlash ? "" : "/"}${baseUrl}`;

      const url = new URL(urlWithBase);
      url.searchParams.set("brnd", brandId);

      const search = url.searchParams.toString();
      const pathWithQuery = `${url.pathname}${search ? `?${search}` : ""}`;

      if (hasProtocol) {
        return `${url.protocol}//${url.host}${pathWithQuery}`;
      }

      if (hasLeadingSlash) {
        return pathWithQuery;
      }

      return pathWithQuery.startsWith("/") ? pathWithQuery.slice(1) : pathWithQuery;
    } catch (error) {
      const separator = baseUrl.includes("?") ? "&" : "?";
      return `${baseUrl}${separator}brnd=${brandId}`;
    }
  }, [mappingsReady, facetMappings, caliber, categoryName, cookies.zip, brandId, caliber_seo_url]);
  
  return (
    <GridItem
      w={{ base: "150px", "1xs": "185px", md: "190px" }}
      h={{ base: "150px", "1xs": "185px", md: "190px" }}
      display={"flex"}
      justifyContent={"center"}
      flexDirection={"column"}
      alignItems={"center"}
      border={"1px solid "}
      borderColor={getCurrentColor(colorMode, "grayC.500", "grayC.2400")}
      bg={getCurrentColor(colorMode, "whiteC.100", "grayC.1700")}
      pt={{ base: "14px", md: "15px" }}
      borderRadius={"5px"}
    >
      <Helmet>
        <link rel="preconnect" as="image" href={caliber_image} />
      </Helmet>

      <Image
        src={caliber_image}
        w={{ base: "70px", "1xs": "116px", md: "120px" }}
        h={{ base: "60px", "1xs": "86px", md: "90px" }}
        alt={`${caliber} ammo`}
        mb={"8px"}
        objectFit={"cover"}
      />

      <Text
        color={getCurrentColor(colorMode, "blackC.100", "whiteC.100")}
        fontFamily={"mainFont"}
        fontWeight={"600"}
        fontSize={"sm"}
        mb={"auto"}
      >
        {caliber}
      </Text>
      <Link
        as={RouterLink}
        py={"10px"}
        display={"flex"}
        w={"100%"}
        justifyContent={"center"}
        alignItems={"center"}
        borderBottomRadius={"5px"}
        to={seoCaliberUrl}
        state={calibersLink(path, caliber, categoryName, cookies.zip)}
        bg={"blueC.100"}
        _hover={{ bg: "blueC.200" }}
        color={"whiteC.100"}
        fontSize={"xs"}
      >
        {total_count} Offers <ArrowForwardIcon color={"whiteC.100"} />
      </Link>
    </GridItem>
  );
};

export default CaliberItem;